import axios from 'axios';
import { storeAction } from '../storeActions.constant';
import { storeGetters } from '../storeGetters.constant';
import { storeMutation } from '../storeMutations.constant';
import { getBasicHeaders } from '@/common/utils/utils';
import { wrapAction } from '@/common/utils/actionLifecycle';
import { roles } from "@/components/appCommon.constants";
import flatten from "lodash/flatten";
import sortBy from "lodash/sortBy";

const chapterStudioState = {
    baseURL: process.env.VUE_APP_BASE_URL,
    chapterList: [],
    resources: [],
    favoriteResources: [],
    selectedChapter: null,
    announcements: null,
};

const chapterStudioMutations = {
    [storeMutation.setChapterStudioResources]: (state, resourceData) => {
        state.resources = resourceData;
        state.favoriteResources = [];
        const allFavoriteResourceList = flatten(resourceData.map(res => res.resourceItems))
            .filter(item => item.isFavorite);
        if (allFavoriteResourceList.length) {
            state.favoriteResources = allFavoriteResourceList;
        }
    },
    [storeMutation.setChapterStudioFavorite]: (state, resource) => {
        if (resource && resource.resourceId) {
            if (state.favoriteResources.some(res => res.resourceId === resource.resourceId)) {
                state.favoriteResources = state.favoriteResources.filter(res => res.resourceId !== resource.resourceId);
            } else {
                state.favoriteResources.push(resource);
            }
        }
    },
    [storeMutation.setChapterStudioChapterList]: (state, communities) => {
        if (communities && communities.length) {
            const chapterList = communities
                .filter(community => (community.communityType && community.communityType.toLowerCase() === 'chapter')
                    && (community.roles && community.roles.includes(roles.CHAPTER_MANAGER)));
            if (chapterList.length) {
                state.chapterList = sortBy(chapterList.map(chapter => ({
                    name: chapter.name || '',
                    id: chapter.communityId || '',
                    parentId: chapter.parentId || '',
                })), 'name');
            }
        }
    },
    [storeMutation.setChapterStudioSelectedChapter]: (state, chapter) => {
        state.selectedChapter = chapter || null;
    },
    [storeMutation.setChapterStudioAnnouncements]: (state, announcementData) => {
        state.announcements = announcementData || null;
    },
};

const chapterStudioActions = {
    [storeAction.getChapterStudioResources]: wrapAction(
        storeAction.getChapterStudioResources,
        ({ state, commit }, communityId) => {
            //handling race condition
            return axios({
                url: `${state.baseURL}communities/api/communities/getChapterStudioCategoriesAndResources/${communityId}`,
                // url: `/mocks/chapterStudioResources.json`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
                .then((response) => {
                    if (response && response.data && response.data.resource) {
                        commit(storeMutation.setChapterStudioResources, response.data.resource);
                    }
                    return response;
                })
                .catch((err) => {
                    throw err;
                });
        },
    ),
    [storeAction.addFavoriteToChapterStudio]: wrapAction(
        storeAction.addFavoriteToChapterStudio,
        ({ state, commit }, content) => {
            return axios({
                url: `${state.baseURL}communities/api/communities/saveChapterStudioPersonFavorite`,
                headers: getBasicHeaders(),
                method: 'POST',
                data: content,
            })
                .then((response) => {
                    return response;
                })
                .catch((err) => {
                    // this will remove from state is any failure happens
                    commit(storeMutation.setChapterStudioFavorite, content);
                    throw err;
                });
        },
    ),
    [storeAction.getChapterStudioAnnouncements]: wrapAction(
        storeAction.addFavoriteToChapterStudio,
        ({ state, commit }, regionId) => {
            return axios({
                url: `${state.baseURL}communities/api/communities/getAnnouncementsByRegionId/${regionId}`,
                //url: `/mocks/chapterStudioAnnouncements.json`,
                headers: getBasicHeaders(),
                method: 'GET',
            })
                .then((response) => {
                    commit(storeMutation.setChapterStudioAnnouncements, response.data);
                    return response;
                })
                .catch((err) => {
                    // this will remove from state is any failure happens
                    commit(storeMutation.setChapterStudioAnnouncements);
                    throw err;
                });
        },
    ),
};

const chapterStudioGetters = {
    [storeGetters.chapterStudioChapterList]: state => state.chapterList,
    [storeGetters.chapterStudioResources]: state => state.resources,
    [storeGetters.chapterStudioFavorites]: state => state.favoriteResources,
    [storeGetters.chapterStudioSelectedChapter]: state => state.selectedChapter,
    [storeGetters.chapterStudioAnnouncements]: state => state.announcements,
    [storeGetters.chapterStudioFavoriteResourceIds]: state => (state.favoriteResources.length ? state.favoriteResources
        .map(res => res.resourceId) : []),
};

export default {
    namespaced: true,
    state: {
        initialState: chapterStudioState,
        ...chapterStudioState,
    },
    mutations: { ...chapterStudioMutations },
    actions: { ...chapterStudioActions },
    getters: { ...chapterStudioGetters },
};
